import { isVideoSource, type RendererSource } from '@signa/renderer';
import {
  positionOf,
  startTimeline,
  stopTimeline,
  STOPPED_TIMELINE,
  type Timeline,
} from '@signa/shared';

/**
 * ビューアの再生操作の判断(#159)。
 *
 * 正本は [#33](../../../../docs/issues/33-viewer-embed/DESIGN.md)。
 *
 * ## なぜシェルから切り出すのか
 *
 * [ADR-0019](../../../../docs/adr/0019-dev-foundation.md) により **DOM 構築は
 * jsdom で試さない。** 判断がシェルの DOM 操作に埋まっていると**検査できない** ——
 * 「静止画しか当たっていないのに再生ボタンが出る」といった間違いに気付けなくなる。
 *
 * **すべて純粋関数。** `<video>` の形(`currentTime` / `duration`)しか見ない。
 *
 * 位置をそろえる計算そのものは [`@signa/shared` の `playback.ts`](../../../../shared/src/playback.ts)
 * にある(S04 と共有 —— #123 / #159)。
 */

/** 割り当てのうち、再生の対象になるもの。 */
export interface Playable {
  readonly source: RendererSource;
}

/**
 * 再生・停止・同期が効く素材だけを取り出す。
 *
 * **静止画は除く**(#158)。`<img>` に `currentTime` は無く、
 * 触ると意味のない操作になる。
 *
 * **同じ要素は 1 度しか返さない**(#168)。共有すると 1 本の `<video>` が
 * 複数のセルに載るので、そのまま写すと**同じ要素に `play()` や
 * `currentTime = 0` をセルの数だけ掛ける。** 本数としても実態と合わない。
 */
export function playableOf(assignments: Iterable<Playable>): HTMLVideoElement[] {
  const videos = new Set<HTMLVideoElement>();
  for (const { source } of assignments) {
    if (isVideoSource(source)) videos.add(source);
  }
  return [...videos];
}

/** 操作 UI の出し方。 */
export interface ControlsState {
  /**
   * ボタンを出すか。
   *
   * **動画が 1 本も無ければ出さない。** 押しても何も起きないボタンは、
   * 「壊れている」と読める —— 静止画だけを当てた状態も含む(#158)。
   */
  readonly visible: boolean;
  /** 「再生」を押せるか。**再生中は押せない。** */
  readonly canPlay: boolean;
  /** 「停止」を押せるか。**止まっていれば押せない。** */
  readonly canPause: boolean;
  /**
   * 「同期」を押せるか。
   *
   * **止まっていても押せる。** 頭をそろえてから流し始める使い方があるため。
   */
  readonly canSync: boolean;
}

/**
 * 操作 UI の状態を決める。
 *
 * `playing` は**シェルが持つ**(利用者が押した結果)。`<video>` の `paused` を
 * 直接見ない —— **1 本でも再生に失敗すると食い違う**し、
 * ループの切れ目で一瞬 `paused` になる実装がある。
 */
export function controlsState(videoCount: number, playing: boolean): ControlsState {
  const visible = videoCount > 0;
  return {
    visible,
    canPlay: visible && !playing,
    canPause: visible && playing,
    canSync: visible,
  };
}

/**
 * 注意表示(「素材はサーバに送信されません」)を出すか(#159)。
 *
 * **素材を当てるまで。** 当てた後まで出し続ける必要はない(利用者の判断 /
 * 2026-08-10)—— [#33](../../../../docs/issues/33-viewer-embed/DESIGN.md) は
 * 当初「ドロップ後も消さない」と決めていたが、社内での確認用途では
 * **場所を取るだけ**になる。
 *
 * **すべて解除したら戻す。** 消えたままだと「送信されたのでは」と読める ——
 * 元の決定が守ろうとしていたのはここである。
 */
export function showsNoUploadNotice(assignedCount: number): boolean {
  return assignedCount === 0;
}

/** 書き出しの操作の出し方(#180 / [ADR-0036](../../../../docs/adr/0036-video-export-webcodecs.md))。 */
export interface ExportControlsState {
  /**
   * 静止画(PNG)の保存を出すか。
   *
   * **動画が 1 本も無く、画像が入っているときだけ出す**(同意書 Q2)。
   *
   * **動く要素が無いので動画にする意味が薄い**が、「今の見え方を 1 枚で渡したい」は
   * 成立する —— そこで**動画の書き出しの代わりに**静止画保存を勧める。
   *
   * **素材が 0 なら出さない。** 押しても何も起きないボタンは「壊れている」と読める ——
   * 再生 / 停止 / 同期([`controlsState`](#controlsState))と同じ方針である(同意書 Q1)。
   */
  readonly canSaveImage: boolean;
  /**
   * 動画の書き出しを出すか。
   *
   * **動画が 1 本でもあれば出す**(#181)。**素材が 0 なら出さない** ——
   * 押しても何も起きないボタンを置かない(同意書 Q1)。
   *
   * **一部のセルが空でも出す。** 押したときに確認を出して続行できる
   * ([`needsPartialConfirm`](#needsPartialConfirm))—— **「足りないから押させない」にはしない。**
   */
  readonly canExportVideo: boolean;
}

/**
 * 書き出す前に確認が要るか(#181 / 同意書)。
 *
 * **一部のセルだけ埋まっているとき**に「このまま書き出しますか」と尋ねる。
 *
 * **数えるのはセル**(#152 / [ADR-0029](../../../../docs/adr/0029-panel-split-grid.md))——
 * 盤面は分割できるので、**1 つの枠に複数の割り当て先がある。**
 * 枠で数えると「分割した枠の 1 セルだけ埋まっている」を「埋まっている」と誤る。
 *
 * **全部埋まっていれば尋ねない。** 何も足りていないわけではないのに確認を出すと、
 * **押すたびに 1 手増えるだけ**になる。
 */
export function needsPartialConfirm(assignedCount: number, cellCount: number): boolean {
  if (assignedCount <= 0) return false;
  if (!Number.isFinite(cellCount) || cellCount <= 0) return false;
  return assignedCount < cellCount;
}

/**
 * 書き出しの操作の状態を決める。
 *
 * `videoCount` は [`playableOf`](#playableOf) の本数(**静止画を除き、同じ要素は 1 度だけ**)、
 * `assignedCount` は**セル単位**の割り当て数(#152 でセル単位になった)。
 *
 * **動画があるときは静止画保存を出さない。** その場合は動画の書き出し(#181)が出るので、
 * **操作を 2 つ並べない** —— ビューアの操作列は再生 / 停止 / 同期で既に 3 つある。
 *
 * **`downloadEnabled` は共有リンクの設定**(#229 / [ADR-0041](../../../../docs/adr/0041-share-link-verification.md) の決定 6)。
 * **`false` なら動画も静止画も出さない。** 埋め込み先の `data-signa-export` は
 * **その中での指定**で、属性で覆せてはならない —— 発行する側(社内)が
 * 「この客先には書き出させない」と決めたのだから。
 *
 * **統制ではない。** クライアント側の出し分けなので、**JS を書き換えれば
 * 書き出せる**。「出さない」であって「させない」ではない。
 * **既定は `true`**(#229 より前の応答を読んでも壊れない)。
 */
export function exportControlsState(
  videoCount: number,
  assignedCount: number,
  downloadEnabled = true,
): ExportControlsState {
  return {
    canSaveImage: downloadEnabled && videoCount === 0 && assignedCount > 0,
    canExportVideo: downloadEnabled && videoCount > 0,
  };
}

/**
 * 再生の状態(#159)。
 *
 * **`playing` と `timeline` を一緒に動かす。** 別々に持つと
 * **「再生中なのに時間軸が止まっている」**という食い違いができ、
 * 次に「再生」を押したときに全部が頭へ巻き戻る。
 *
 * **不変条件: `playing === (timeline.startedAt !== null)`。**
 * ここを純関数にしてあるのは、[ADR-0019](../../../../docs/adr/0019-dev-foundation.md) により
 * シェルの状態遷移を jsdom で試せないため —— 上の食い違いは実際に踏んだ。
 */
export interface PlaybackState {
  readonly playing: boolean;
  readonly timeline: Timeline;
}

/** 止まっている状態。 */
export const STOPPED_PLAYBACK: PlaybackState = { playing: false, timeline: STOPPED_TIMELINE };

/**
 * 再生を始める(利用者が押した)。
 *
 * `fromStart` は「同期」—— **時間軸を先頭に戻す。**
 * そうでなければ止めた位置から続ける。
 */
export function startedPlayback(
  state: PlaybackState,
  now: number,
  fromStart: boolean,
): PlaybackState {
  return {
    playing: true,
    timeline: startTimeline(now, fromStart ? 0 : state.timeline.baseTime),
  };
}

/** 止める。**位置は保つ**(「再生」で続きから)。 */
export function stoppedPlayback(state: PlaybackState, now: number): PlaybackState {
  return { playing: false, timeline: stopTimeline(state.timeline, now) };
}

/**
 * 自動再生で始まった(素材を当てた直後)。
 *
 * **すでに走っていればそのまま。** 走っていなければ時間軸を動かす ——
 * ここを忘れると**見張りが何もせず、次の「再生」で全部が頭へ戻る。**
 */
export function autoStartedPlayback(state: PlaybackState, now: number): PlaybackState {
  if (state.timeline.startedAt !== null) return { ...state, playing: true };
  return { playing: true, timeline: startTimeline(now, state.timeline.baseTime) };
}

/**
 * いま当てた素材を、走っている時間軸に合わせるべき位置。
 *
 * **走っていなければ `null`**(合わせる必要がない)。合わせないと
 * **当てた順にずれる** —— #123 で報告された症状そのもの。
 */
export function joinPositionOf(state: PlaybackState, now: number): number | null {
  return state.timeline.startedAt === null ? null : positionOf(state.timeline, now);
}
